import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

import {
  CreateSireRequest,
  CreateSireResult,
  SireDetail,
  SireDuplicateCheckResult,
  UpdateSireRequest,
} from './types';

export type {
  CreateSireRequest,
  CreateSireResult,
  SireDetail,
  SireDuplicateCheckResult,
  UpdateSireRequest,
} from './types';

export const sireQueryKey = (sireId: string) => ['sire', sireId] as const;

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchSire(
  token: AccessToken,
  sireId: string,
): Promise<SireDetail> {
  const response = await fetch(`${getBaseUrl()}/api/v1/sire/${sireId}`, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

export const sireDuplicateCheckOptions = (
  token: AccessToken,
  {
    breedId,
    shortName,
  }: {
    breedId: string;
    shortName: string;
  },
) =>
  queryOptions({
    queryKey: ['sire', 'duplicate-check', { breedId, shortName }],
    staleTime: 0,
    gcTime: 0,
    enabled: !!token,
    async queryFn(): Promise<SireDuplicateCheckResult> {
      const searchParams = new URLSearchParams({ breedId, shortName });
      const response = await fetch(
        `${getBaseUrl()}/api/v1/sire/duplicate-check?${searchParams.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: getHeaders(token),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return (await response.json()) as SireDuplicateCheckResult;
    },
  });

export async function createSire(
  token: AccessToken,
  request: CreateSireRequest,
): Promise<CreateSireResult> {
  const response = await fetch(`${getBaseUrl()}/api/v1/sire`, {
    method: 'POST',
    credentials: 'include',
    headers: getHeaders(token),
    body: JSON.stringify(request),
  });

  if (response.status === 409 || response.status === 400) {
    return { success: false, message: await response.text() };
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return { success: true, sire: await response.json() };
}

export async function updateSire(
  token: AccessToken,
  sireId: string,
  request: UpdateSireRequest,
): Promise<SireDetail> {
  const response = await fetch(`${getBaseUrl()}/api/v1/sire/${sireId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: getHeaders(token),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

export function useSireQuery(sireId: string | undefined, enabled = true) {
  const token = useAccessToken();
  return useQuery({
    queryKey: sireQueryKey(sireId ?? ''),
    queryFn: () => fetchSire(token, sireId!),
    enabled: !!token && enabled && Boolean(sireId),
  });
}

export function useCreateSireMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (request: CreateSireRequest) => createSire(token, request),
  });
}

export function useUpdateSireMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: ({
      sireId,
      request,
    }: {
      sireId: string;
      request: UpdateSireRequest;
    }) => updateSire(token, sireId, request),
    async onSuccess(sire, _variables, _ctx, { client }) {
      client.setQueryData(sireQueryKey(sire.id), sire);
    },
  });
}
